(ns hai.web-app.util.fetch
  "HTTP fetching utilities

  Provides three levels of API:

  - `request`/`get`/`post`/`put`/`delete` - lambdaisland.fetch style
    ClojureScript wrappers for js/fetch

  - (reagent/with-let [res (!fetch {:url \"/me\"})]
      (let [{:keys [loading? error? body status headers]} @res]
        )) — reagent style

  - (>e [:fetch {:url \"http://example.com\"
                 :store-in [:my :result]}]) — re-frame style

  "
  (:refer-clojure :exclude [get])
  (:require
   [hai.builder-ide.transit :as transit]
   [hai.ui.rf-sugar :refer [>e defeffect defevent-fx defsub]]
   [hai.web-app.util.rf-storage :as rf-storage]
   [kitchen-async.promise :as p]
   [lambdaisland.fetch :as fetch]
   [reagent.core :as reagent]
   [clojure.walk :as walk]))

(defn request [url & [opts]]
  (let [body (:body opts)]
    (if (instance? js/FormData body)
      ;; For FormData, use raw js/fetch to avoid lambdaisland.fetch's body processing
      (p/let [response (js/fetch url (clj->js {:method (:method opts :get)
                                               :body   body}))
              content-type (-> response .-headers (.get "content-type"))
              body-text    (.text response)
              ;; Try to parse response based on content-type
              parsed-body (cond
                            (and content-type (re-find #"application/.*json" content-type))
                            (js->clj (js/JSON.parse body-text) :keywordize-keys true)

                            :else
                            body-text)]
        {:status  (.-status response)
         :headers (js->clj (.-headers response))
         :body    parsed-body})
      ;; For everything else, use lambdaisland.fetch with Transit
      (fetch/request url (assoc opts
                                :transit-json-reader (transit/reader)
                                :transit-json-writer (transit/writer))))))

(def get request)

(defn post [url & [opts]]
  (request url (assoc opts :method :post)))

(defn put [url & [opts]]
  (request url (assoc opts :method :put)))

(defn delete [url & [opts]]
  (request url (assoc opts :method :delete)))

(defn head [url & [opts]]
  (request url (assoc opts :method :head)))

(defevent-fx :fetch/start [{:keys [db]} {:keys [request-id request]}]
  {:db (-> db
           (assoc-in [:fetch/request request-id :state] :start)
           (assoc-in [:fetch/request request-id :request] request))})

(defevent-fx :fetch/error
  [{:keys [db]} {:keys [request-id request on-error-fx]} err]
  (if (contains? (:resolve/canceled-requests db) request-id)
    {:db (-> db
             (update :resolve/canceled-requests disj request-id)
             (update :fetch/request dissoc request-id))}
    {:db (cond-> db
           request-id
           (assoc-in [:fetch/request request-id] {:state :error
                                                  :err err
                                                  :request request}))
     :fx (walk/postwalk-replace
          {'?body (:body err)
           '?status (:status err)
           '?response err}
          on-error-fx)}))

(defevent-fx :fetch/success <-> [(rf-storage/settings-local)]
  [{:keys [db]} {:keys [request-id request store-in transform-fn on-success-fx]} resp]
  (if (contains? (:resolve/canceled-requests db) request-id)
    {:db (-> db
             (update :resolve/canceled-requests disj request-id)
             (update :fetch/request dissoc request-id))}
    (let [result (transform-fn (:body resp))]
      {:db (cond-> db
             store-in
             (assoc-in store-in result)
             request-id
             (assoc-in [:fetch/request request-id] {:state :success
                                                    :response resp
                                                    :request request}))
       :fx (walk/postwalk-replace
            {'?body (:body resp)
             '?result result
             '?response resp}
            on-success-fx)})))

(defn- pathom-error?
  "Whether a 2xx response is really a failure — `/resolve` reports Pathom
  processing errors in the body rather than in the status."
  [resp]
  (some? (get-in resp [:body :com.wsscode.pathom3.error/error-message])))

;; on-success-fx is a re-frame fx vector
;; on-success is a plain (presumably side-effectful & ideally non-blocking) function of 1-arg (the response body)
(defeffect :fetch [{:keys [store-in transform-fn on-success-fx on-error-fx on-success request-id]
                    :or {transform-fn identity
                         on-success-fx []
                         on-error-fx []
                         request-id (gensym "fetch-")}
                    :as opts}]
  (let [req (dissoc opts :store-in :transform-fn :on-success-fx :on-error-fx)]
    (>e [:fetch/start {:request-id request-id
                       :request req}])
    (p/try
      (p/let [resp (request (:url req) (dissoc req :url))]
        (if (and (<= 200 (:status resp) 299)
                 (not (pathom-error? resp)))
          (do
            (>e [:fetch/success
                 {:request-id request-id
                  :store-in store-in
                  :transform-fn transform-fn
                  :on-success-fx on-success-fx
                  :request req}
                 resp])
            (when on-success
              ;; ideally NOT be a blocking call (e.g. http)
              ;; printing or swapping an atom is fine
              (on-success (:body resp))))
          (>e [:fetch/error
               {:request-id request-id
                :on-error-fx on-error-fx
                :request req}
               resp])))
      (p/catch :default e
        (>e [:fetch/error
             {:request-id request-id
              :on-error-fx on-error-fx
              :request req}
             e])))))

(defevent-fx :fetch [cofx opts]
  {:fetch opts})

(defevent-fx :fetch-once [cofx opts]
  (when-not (get-in cofx [:db :fetch/request (:request-id opts)])
    {:fetch opts}))

(defevent-fx :fetch/reload [cofx request-id]
  (when-let [req (get-in cofx [:db :fetch/request request-id :request])]
    {:fetch req}))

(defsub :fetch/request [db request-id]
  (get-in db [:fetch/request request-id]))

(defsub :fetch/state [db request-id]
  (get-in db [:fetch/request request-id :state]))

(defsub :fetch/response [db request-id]
  (get-in db [:fetch/request request-id :response]))

(defsub :fetch/body [db request-id]
  (get-in db [:fetch/request request-id :response :body]))

(defsub :fetch/header [db request-id header-key]
  (get-in db [:fetch/request request-id :response :headers header-key]))

(defsub :fetch/error [db request-id]
  (get-in db [:fetch/request request-id :err]))

(defn fetch->ratom
  "Turn a fetch promise into a reagent atom with error-val, error?, loading?, and data states"
  ([p]
   (fetch->ratom p {}))
  ([p {:keys [default error-val ratom] :or {error-val ::error} :as opts}]
   (reagent/with-let [r (or ratom (reagent/atom nil))
                      on-result (fn [{:keys [status headers body]}]
                                  (reset! r {:error? (not (<= 200 status 299))
                                             :loading? false
                                             :status status
                                             :body body
                                             :headers headers}))]
     (reset! r {:loading? true
                :status nil
                :headers {}
                :body default
                :error? false})
     (p/then p on-result on-result)
     r)))

(defn !fetch [url & [opts]]
  (fetch->ratom (request url opts)))

;; TODO This feels a bit off but it's nice to be able to call resolve as an effect
(defeffect :resolve
  [{:keys [query] :as args}]
  (>e [:fetch (-> args
                  (assoc :url "/resolve"
                         :method :post
                         :body {:query query})
                  (dissoc :query))]))

(defevent-fx :resolve
  ;; NOTE args after the first cofx arg become vector destructuring, this event can be called with 2
  ;; or 3 args
  [_ query args]
  {:resolve (assoc args :query query)})
